/**
 * Web money boundary — single chokepoint for converting between the
 * three legal money string shapes documented in AGENTS.md \u00a7 1.
 *
 * Use these helpers throughout `apps/web` instead of calling
 * `Money.of` / `Money.fromMinor` / `formatMoney` directly. Centralising
 * the boundary makes it grep-friendly and keeps the wire-vs-decimal
 * distinction obvious at every call site.
 *
 * If you find yourself reaching for `Number(value)` or `parseFloat`
 * around a money amount, stop and ask: "what shape is this string?"
 *  - From the API / database  -> `liftWireMoney(...)`
 *  - From a user input field  -> `parseUserMoney(...)`
 *  - Already a Money value    -> `displayMoney(...)`
 *
 * The 2026-05-04 100x-overcharge bug shipped because someone called
 * `Money.of('100000', 'USD')` on a wire amount — it interpreted the
 * minor-units string as decimal and quoted the customer 100x. The
 * deprecation tag on `Money.of(string)` and these helpers exist to
 * make that bug impossible the second time.
 */
import {
  formatMoney,
  Money,
  parseMoneyInput,
  type CurrencyCode,
  type FormatMoneyOptions,
  type MoneyValue,
  type ParseResult,
} from '@repo/domain';

export type { ParseResult } from '@repo/domain';

/**
 * Lift a wire-format minor-units string (the kind that lives on the
 * `totals.amountDue` of an invoice DTO, etc.) into a Money value.
 *
 * Always use this for strings you read off an API response or database
 * row. Never use `Money.of` for these — it expects a decimal-format
 * string and would silently return 100x the value.
 */
export function liftWireMoney(amount: string, currency: CurrencyCode): MoneyValue {
  return Money.fromMinor(amount, currency);
}

/**
 * Parse a user-typed decimal string into a Money value (or a
 * tagged failure result so the form can surface the error).
 */
export function parseUserMoney(
  raw: string,
  currency: CurrencyCode,
): ParseResult<MoneyValue> {
  return parseMoneyInput(raw, currency);
}

/**
 * Format a Money value for display. Always renders via the active
 * currency's locale-aware rules.
 */
export function displayMoney(
  money: MoneyValue,
  options?: FormatMoneyOptions,
): string {
  return formatMoney(money, options);
}

/**
 * Convenience: lift + display in one call. Saves the typical pattern
 * `displayMoney(liftWireMoney(amount, currency))` inside JSX.
 */
export function displayWireMoney(
  amount: string,
  currency: CurrencyCode,
  options?: FormatMoneyOptions,
): string {
  return displayMoney(liftWireMoney(amount, currency), options);
}

/**
 * Best-effort currency-symbol glyph for use as an input prefix
 * (`$`, `€`, `¥`…). Falls back to the ISO code when Intl can't
 * produce a glyph (some narrow / less common currencies). Use it
 * for ornament only — never round-trip through this for math.
 */
export function currencySymbol(currency: CurrencyCode): string {
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    const found = parts.find((p) => p.type === 'currency');
    return found?.value ?? currency;
  } catch {
    return currency;
  }
}
